import React, { useState, useEffect, useRef } from "react";
import Tooltip from "@mui/material/Tooltip";

function detectTouchDevice() {
  if (typeof window === "undefined") return false;
  if (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) return true;
  try {
    if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches)
      return true;
  } catch (e) {}
  if ("ontouchstart" in window) return true;
  return false;
}

export default function CustomTooltip({
  children,
  maxWidth = "25rem",
  ...tooltipProps
}) {
  const [open, setOpen] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const tooltipRef = useRef(null);

  useEffect(() => {
    // run once on mount (SSR-safe)
    setIsTouchDevice(detectTouchDevice());
  }, []);

  const handleToggle = (e) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  const handleClose = () => setOpen(false);
  const handleOpen = () => setOpen(true);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }
    return () => document.removeEventListener("click", handleClickOutside);
  }, [open]);

  return (
    <span ref={tooltipRef}>
      <Tooltip
        {...tooltipProps}
        open={open}
        onClose={handleClose}
        disableFocusListener
        disableTouchListener
        enterTouchDelay={0}
        leaveTouchDelay={150}
        PopperProps={{
          modifiers: [
            { name: "offset", options: { offset: [0, 8] } },
            {
              name: "flip",
              options: { fallbackPlacements: ["left", "top", "bottom"] },
            },
            { name: "preventOverflow", options: { boundary: "viewport" } },
          ],
        }}
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "white",
              color: "#6C6C6C",
              fontSize: "10px",
              border: "1px solid #E7E7E7",
              borderRadius: "2px",
              maxWidth: maxWidth,
              whiteSpace: "normal",
              marginLeft: "2px !important",
              boxShadow: "0 0 24px rgba(0, 0, 0, 0.2)",
              // Responsive behavior
              "@media (max-width: 768px)": {
                maxWidth: "90vw", // use 90% of viewport width on tablets/phones
                fontSize: "11px", // slightly larger for readability
                padding: "6px 8px",
              },
              "@media (max-width: 480px)": {
                maxWidth: "95vw", // near full width on very small screens
                fontSize: "12px",
                lineHeight: 1.3,
              },
            },
          },
          arrow: {
            sx: {
              "&::before": {
                border: "2px solid #E7E7E7",
                backgroundColor: "white",
                boxSizing: "border-box",
              },
            },
          },
        }}
      >
        <span
          className="cursor-pointer"
          onClick={handleToggle}
          onMouseEnter={!isTouchDevice ? handleOpen : undefined}
          onMouseLeave={!isTouchDevice ? handleClose : undefined}
        >
          {children}
        </span>
      </Tooltip>
    </span>
  );
}
